import { OrganizationRoleType } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import {
  canUpdateOrgEntry,
  hasContractCreateAccess,
  hasContractViewAccess,
  hasProjectCreateAccess,
  hasProjectViewAccess,
  hasSourcingCreateAccess,
  hasSourcingViewAccess,
  isCreator,
} from "../auth/utils";
import { isAppShellPath } from "../layout/isAppShellPath";
import { useViewAsRole } from "./viewAsRoleStore";

export const useSub = () => {
  const authState = useLoggedInWithOrgContextState();
  const viewAsRole = useViewAsRole();

  if (!authState) {
    throw Error("Auth state with org context not available");
  }

  const org = authState.selectedOrg;
  const { hasBuyerAccess, hasContractAccess, hasProjectAccess } = org.subscription;
  const isSupplier = !(hasBuyerAccess || hasContractAccess || hasProjectAccess);
  const previewInScope = isAppShellPath(window.location.pathname);
  const previewedRole = !isSupplier && previewInScope && org.canEdit ? viewAsRole : null;

  const clampedByPreview = (grantedByBff: boolean, previewedRoleAllows: (roles: OrganizationRoleType[]) => boolean) =>
    grantedByBff && (previewedRole === null || previewedRoleAllows([previewedRole]));
  const unlessPreviewingARole = (grantedByBff: boolean) => grantedByBff && previewedRole === null;

  const viewsSourcingEvents = clampedByPreview(org.canViewSourcingEvents, hasSourcingViewAccess);
  const viewsContracts = clampedByPreview(org.canViewContracts, hasContractViewAccess);
  const canViewContracts = hasContractAccess && viewsContracts;

  return {
    canUpdateOrganization: unlessPreviewingARole(org.canEdit),
    canEditDocumentTemplates: unlessPreviewingARole(org.canEditDocumentTemplates),
    canConfigureContracts: unlessPreviewingARole(org.canConfigureContracts),
    canCreateContractTemplates: unlessPreviewingARole(org.canCreateContractTemplate),
    canViewSourcingEvents: isSupplier || (hasBuyerAccess && viewsSourcingEvents),
    canViewContracts,
    viewsSourcingEvents,
    viewsContracts,
    viewsSharedContracts: isSupplier || (org.receivesContractShares && !canViewContracts),
    canViewProjects: hasProjectAccess && clampedByPreview(org.canViewProjects, hasProjectViewAccess),
    canCreateProjects: clampedByPreview(org.canCreateProject, hasProjectCreateAccess),
    canReportNeeds: org.canReportNeed,
    canAssessNeeds: org.subscription.hasNeedsAccess && (isSupplier || viewsSourcingEvents),
    canUseContractSearch: org.subscription.hasFencyContractSearchAccess && hasContractAccess && viewsContracts,
    hasContractAccess: org.subscription.hasContractAccess,
    hasBuyerAccess: org.subscription.hasBuyerAccess,
    isSupplier,
    canBid: !hasBuyerAccess,
    hasSignatureAccess: org.subscription.hasSignatureAccess,
    hasBankIdAccess: org.subscription.hasBankIdAccess,
    canCreateEvents: hasBuyerAccess && clampedByPreview(org.canCreateEvent, hasSourcingCreateAccess),
    canCreateContracts: clampedByPreview(org.canCreateContract, hasContractCreateAccess),
    canUpdateOrganizationEntries: clampedByPreview(org.canUpdateOrganizationEntries, canUpdateOrgEntry),
    canUpdateContactPersons: clampedByPreview(org.canUpdateContactPersons, canUpdateOrgEntry),
    canCreateOrganizationAssessments: clampedByPreview(org.canCreateOrganizationAssessments, isCreator),
    hasProductAccess: org.hasProductAccess,
    hasProjectAccess: org.hasProjectAccess,
    hasAiAccess: org.hasAiAccess,
    hasFencyContractSearchAccess: org.subscription.hasFencyContractSearchAccess,
    hasAssistantAccess: org.subscription.hasAssistantAccess,
    hasAiBidInsightAccess: org.subscription.hasAiBidInsightAccess,
    hasNeedsAccess: org.subscription.hasNeedsAccess,
    fencySyncEnabled: org.subscription.fencySyncEnabled,
    hasDocumentTemplateAccess: org.hasDocumentTemplateAccess,
    hasIFRSAccess: org.hasIFRSAccess,
    hasOrganizationAssessmentAccess: org.subscription.hasOrganizationAssessmentAccess,
    hasNegotiationAcces: org.subscription.hasNegotiationAccess,
  };
};

export type SubAccess = ReturnType<typeof useSub>;
